/**
 * What THIS device actually lays out: the strips iOS takes, the strips the
 * shell applies, the chrome bar, the canvas, and how much of the lowest
 * control on the page is hidden. Shown in Navigate > Details and sent to
 * every connected OPBridge's doc store as `__frame_report_<device>`, where it
 * lands on that machine's disk.
 *
 * WHY (2026-10-05). Controls dragged to the bottom on the desktop were cut off
 * on the iPad. The fix made the desktop give up the same strips the iPad does
 * (devices.ts `deviceReserve`), and the desktop then clamped a control to a
 * 748pt canvas - and the operator reported it STILL slightly cut off on the
 * iPad. The 748 was derived by emulating the iPad on the desktop, never
 * measured on it. Everything about the device's geometry has been wrong at
 * least once in this project when inferred (DEC-210/211); this reports it
 * instead, from the device, with no screenshot and no reading-out-loud.
 *
 * Read-only on the layout. The only write is the report doc itself, and only
 * when the operator opens Details - never on load.
 */

import { bridges, getConnection } from '../opbridge/store.ts';
import { measureViewportBox, safeAreaInsets } from './trueViewport.ts';

export interface FrameReport {
  at: string;
  build: string;
  device: string;
  userAgent: string;
  standalone: boolean;
  screen: { w: number; h: number; dpr: number };
  window: { innerW: number; innerH: number; layoutH: number; unitH: number };
  /** Raw `env(safe-area-inset-*)`, resolved to px by a probe element. */
  iosInsets: { top: number; right: number; bottom: number; left: number };
  /** The strips the shell actually applies (its computed padding). */
  shellStrips: { top: number; bottom: number };
  /** The project's strips as App.tsx set them on the shell: recorded by the
   *  playing device (`Project.reserve`), else the device table. */
  tableStrips: { top: number; bottom: number };
  shell: { w: number; h: number; scale: number; screenTop: number; screenBottom: number };
  /** In shell points (unscaled). */
  bar: { top: number; h: number };
  canvas: { top: number; w: number; h: number; screenBottom: number };
  /** The lowest control on the active page, or null when the page is empty. */
  lowest: null | { kind: string; id: string; bottom: number; hiddenBelowCanvas: number; hiddenBelowScreen: number };
}

function px(v: string): number {
  const n = parseFloat(v);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
}

export function deviceTag(): string {
  const ua = navigator.userAgent;
  // iPadOS reports a Mac user agent; a touch screen is what tells them apart.
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'ipad';
  if (/iPhone/.test(ua)) return 'iphone';
  if (/Macintosh/.test(ua)) return 'mac';
  if (/Windows/.test(ua)) return 'windows';
  return 'other';
}

/** Measure, or null when there is no project canvas on screen. */
export function measureFrame(): FrameReport | null {
  const shell = document.querySelector('.shell') as HTMLElement | null;
  const bar = document.querySelector('.shell > .hdr') as HTMLElement | null;
  const inner = document.querySelector('.scaled-canvas-inner') as HTMLElement | null;
  if (!shell || !bar || !inner) return null;
  const sr = shell.getBoundingClientRect();
  const scale = shell.offsetWidth > 0 ? sr.width / shell.offsetWidth : 1;
  const scs = getComputedStyle(shell);
  const br = bar.getBoundingClientRect();
  const ir = inner.getBoundingClientRect();
  const box = measureViewportBox();
  const toShell = (screenY: number) => Math.round(((screenY - sr.top) / scale) * 100) / 100;

  let lowest: FrameReport['lowest'] = null;
  for (const el of Array.from(inner.querySelectorAll<HTMLElement>('.editable-item'))) {
    const r = el.getBoundingClientRect();
    const bottom = toShell(r.bottom);
    if (!lowest || bottom > lowest.bottom) {
      lowest = {
        kind: el.dataset.kind ?? '?',
        id: el.dataset.itemId ?? '?',
        bottom,
        hiddenBelowCanvas: Math.max(0, Math.round(((r.bottom - ir.bottom) / scale) * 100) / 100),
        // The true box, not innerHeight: innerHeight is the short, corrupted
        // value on an installed iPad (WebKit #254868, trueViewport.ts).
        hiddenBelowScreen: Math.max(0, Math.round((r.bottom - (box ? box.h : window.innerHeight)) * 100) / 100),
      };
    }
  }

  return {
    at: new Date().toISOString(),
    build: __BUILD_SHA__,
    device: deviceTag(),
    userAgent: navigator.userAgent,
    standalone: box ? box.standalone : false,
    screen: { w: screen.width, h: screen.height, dpr: window.devicePixelRatio || 1 },
    window: {
      innerW: window.innerWidth, innerH: window.innerHeight,
      layoutH: box ? box.layoutH : 0, unitH: box ? box.unitH : 0,
    },
    iosInsets: safeAreaInsets(),
    shellStrips: { top: px(scs.paddingTop), bottom: px(scs.paddingBottom) },
    tableStrips: { top: px(shell.style.getPropertyValue('--frame-reserve-top')), bottom: px(shell.style.getPropertyValue('--frame-reserve-bottom')) },
    shell: {
      w: shell.offsetWidth, h: shell.offsetHeight, scale: Math.round(scale * 10000) / 10000,
      screenTop: Math.round(sr.top * 100) / 100, screenBottom: Math.round(sr.bottom * 100) / 100,
    },
    bar: { top: toShell(br.top), h: Math.round((br.height / scale) * 100) / 100 },
    canvas: { top: toShell(ir.top), w: inner.clientWidth, h: inner.clientHeight, screenBottom: Math.round(ir.bottom * 100) / 100 },
    lowest,
  };
}

/** Write the report to every connected OPBridge. Resolves to how many took it. */
export async function sendFrameReport(report: FrameReport): Promise<{ ok: number; of: number }> {
  const docId = `__frame_report_${report.device}`;
  let ok = 0;
  let of = 0;
  for (const cfg of bridges()) {
    const conn = getConnection(cfg.id);
    if (!conn || conn.status() !== 'connected') continue;
    of++;
    const client = conn.httpClient();
    try {
      let baseRev = 0;
      try { baseRev = (await client.docGet('opbuttons', docId)).rev; } catch { /* 404: first report */ }
      await client.docPut('opbuttons', docId, { base_rev: baseRev, body: report });
      ok++;
    } catch { /* a report must never disturb the app */ }
  }
  return { ok, of };
}
